class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

(() => {
  const PACK_PRODUCTS = [
    {
      productId: 9309989503030,
      handle: 'tarjetero-victorias-secret-plateado',
    },
    {
      productId: 9333215625270,
      handle: 'llavero-munequera-victorias-secret',
    },
  ];

  const SUGGESTION_SELECTOR = '[data-pack-plata-suggestion]';
  let refreshTimer;
  let refreshSequence = 0;

  const ensureStyles = () => {
    if (document.getElementById('PackPlataSuggestionStyles')) return;

    const style = document.createElement('style');
    style.id = 'PackPlataSuggestionStyles';
    style.textContent = `
      .pack-plata-suggestion {
        margin: 0.8rem 0 1.2rem;
        padding: 1.2rem;
        border: 0.1rem solid rgba(var(--color-foreground), 0.14);
        border-radius: 1.4rem;
        background: rgba(var(--color-foreground), 0.03);
      }
      .pack-plata-suggestion__heading {
        margin: 0 0 0.9rem;
        font-size: 1.4rem;
        line-height: 1.3;
        font-weight: 700;
      }
      .pack-plata-suggestion__row {
        display: grid;
        grid-template-columns: 6.8rem minmax(0, 1fr);
        gap: 1rem;
        align-items: center;
      }
      .pack-plata-suggestion__image {
        width: 6.8rem;
        height: 6.8rem;
        object-fit: cover;
        border-radius: 1rem;
        background: rgb(var(--color-background));
      }
      .pack-plata-suggestion__title {
        margin: 0;
        color: rgb(var(--color-foreground));
        font-size: 1.35rem;
        line-height: 1.35;
        font-weight: 600;
      }
      .pack-plata-suggestion__copy {
        margin: 0.35rem 0 0;
        color: rgba(var(--color-foreground), 0.7);
        font-size: 1.15rem;
        line-height: 1.35;
      }
      .pack-plata-suggestion__button {
        width: 100%;
        min-height: 4rem;
        margin-top: 1rem;
        padding: 0.9rem 1.4rem;
        border: 0;
        border-radius: var(--buttons-radius-outset);
        background: rgb(var(--color-button));
        color: rgb(var(--color-button-text));
        font: inherit;
        font-size: 1.25rem;
        font-weight: 700;
        letter-spacing: 0.06rem;
        text-transform: uppercase;
        cursor: pointer;
      }
      .pack-plata-suggestion__button[disabled] {
        cursor: wait;
        opacity: 0.65;
      }
      .pack-plata-suggestion__error {
        margin: 0.7rem 0 0;
        color: rgb(var(--color-foreground));
        font-size: 1.15rem;
      }
    `;
    document.head.appendChild(style);
  };

  const fetchCart = async () => {
    const response = await fetch(`${routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('No se pudo leer el carrito.');
    return response.json();
  };

  const fetchProduct = async (handle) => {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}products/${handle}.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('No se pudo cargar el producto sugerido.');
    return response.json();
  };

  const addVariant = async (variantId, button, errorElement) => {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Agregando…';
    errorElement.textContent = '';

    try {
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      formData.append(
        'sections',
        cartDrawer.getSectionsToRender().map((section) => section.id),
      );
      formData.append('sections_url', window.location.pathname);
      config.body = formData;

      const response = await fetch(`${routes.cart_add_url}`, config);
      const state = await response.json();
      if (!response.ok || state.status) {
        throw new Error(state.description || state.message || 'No se pudo agregar el producto.');
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        await publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'pack-plata-suggestion',
          productVariantId: String(variantId),
          cartData: state,
        });
      }

      cartDrawer.renderContents(state);
    } catch (error) {
      errorElement.textContent = error.message || 'No se pudo agregar el producto. Inténtalo nuevamente.';
      button.disabled = false;
      button.textContent = originalText;
    }
  };

  const buildSuggestion = (product, productConfig) => {
    const variant = product.variants?.find((item) => item.available) || product.variants?.[0];
    if (!variant || variant.available === false) return null;

    const wrapper = document.createElement('section');
    wrapper.className = 'pack-plata-suggestion';
    wrapper.dataset.packPlataSuggestion = 'true';
    wrapper.dataset.packProductId = String(productConfig.productId);
    wrapper.setAttribute('aria-label', 'Completa tu Pack Plata');

    const heading = document.createElement('p');
    heading.className = 'pack-plata-suggestion__heading';
    heading.textContent = 'Completa tu Pack Plata 🩶💗';
    wrapper.appendChild(heading);

    const row = document.createElement('div');
    row.className = 'pack-plata-suggestion__row';

    const imageUrl = product.featured_image || product.images?.[0];
    if (imageUrl) {
      const image = document.createElement('img');
      image.className = 'pack-plata-suggestion__image';
      image.src = imageUrl;
      image.alt = product.title || 'Producto sugerido';
      image.loading = 'lazy';
      image.width = 68;
      image.height = 68;
      row.appendChild(image);
    } else {
      const imagePlaceholder = document.createElement('div');
      imagePlaceholder.className = 'pack-plata-suggestion__image';
      row.appendChild(imagePlaceholder);
    }

    const info = document.createElement('div');
    const title = document.createElement('p');
    title.className = 'pack-plata-suggestion__title';
    title.textContent = product.title;
    info.appendChild(title);

    const copy = document.createElement('p');
    copy.className = 'pack-plata-suggestion__copy';
    copy.textContent = 'Agrégalo para completar el pack. El beneficio se reflejará automáticamente en el carrito.';
    info.appendChild(copy);
    row.appendChild(info);
    wrapper.appendChild(row);

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'pack-plata-suggestion__button';
    button.textContent = 'Agregar al pack';
    wrapper.appendChild(button);

    const errorElement = document.createElement('p');
    errorElement.className = 'pack-plata-suggestion__error';
    errorElement.setAttribute('role', 'alert');
    wrapper.appendChild(errorElement);

    button.addEventListener('click', () => addVariant(variant.id, button, errorElement));
    return wrapper;
  };

  const refreshSuggestion = async () => {
    const sequence = ++refreshSequence;
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    try {
      const cart = await fetchCart();
      if (sequence !== refreshSequence) return;

      const productIds = new Set(cart.items.map((item) => Number(item.product_id)));
      const firstInCart = productIds.has(PACK_PRODUCTS[0].productId);
      const secondInCart = productIds.has(PACK_PRODUCTS[1].productId);

      let missingProduct = null;
      if (firstInCart && !secondInCart) missingProduct = PACK_PRODUCTS[1];
      if (secondInCart && !firstInCart) missingProduct = PACK_PRODUCTS[0];

      const currentSuggestion = document.querySelector(SUGGESTION_SELECTOR);
      if (!missingProduct) {
        currentSuggestion?.remove();
        return;
      }

      if (currentSuggestion?.dataset.packProductId === String(missingProduct.productId)) return;
      currentSuggestion?.remove();

      const product = await fetchProduct(missingProduct.handle);
      if (sequence !== refreshSequence) return;

      const suggestion = buildSuggestion(product, missingProduct);
      if (!suggestion) return;

      const footer = document.querySelector('#CartDrawer > .drawer__inner > .drawer__footer');
      if (!footer) return;
      footer.parentElement.insertBefore(suggestion, footer);
    } catch (error) {
      console.error('Pack Plata suggestion:', error);
    }
  };

  const scheduleRefresh = (delay = 0) => {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(refreshSuggestion, delay);
  };

  const initPackSuggestion = () => {
    ensureStyles();
    scheduleRefresh();

    document.addEventListener('click', (event) => {
      if (event.target.closest('#cart-icon-bubble')) scheduleRefresh(30);
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => scheduleRefresh(60));
    }

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      const observer = new MutationObserver(() => {
        if (!document.querySelector(SUGGESTION_SELECTOR)) scheduleRefresh(50);
      });
      observer.observe(cartDrawer, { childList: true, subtree: true });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPackSuggestion, { once: true });
  } else {
    initPackSuggestion();
  }
})();
